<template>
  <div :class="_fs_class">
    <cat-label
      v-if="label"
      :labelId="id + '_lbl'"
      :forId="id + '-attribute-text'"
      :label="label"
      :placeholder="offscreenLabel"
      :optional="!mandatory"
      :errors="errors"
      :readonly="readonly"
      :inline="help && !!help.url"
    ></cat-label>
    <cat-help-trigger
      v-if="help && help.url"
      :sourceId="id"
      :url="help.url"
      :title="label"
    />
    <cat-hint :id="id + '_hint'" :text="hint" />
    <cat-help
      v-if="help && help.text"
      :id="id"
      :heading="help.title"
      :text="help.text"
      :aria-describedby="label && id ? id + '_lbl' : null"
    />
    <slot></slot>
    <div class="input-holder">
      <cat-input
        :id="id + '-attribute-text'"
        :value="model"
        :placeholder="placeholder"
        :dataLabel="label ? label : placeholder"
        :errors="errors && errors.length > 0"
        :mandatory="mandatory"
        :disabled="disabled"
        :readonly="readonly"
        :inputType="inputType"
        :regex="regex"
        :mask="mask"
        :inputMask="inputMask"
        :offscreenLabel="offscreenLabel"
        :maxLength="maxLength"
        :labelHandled="typeof label !== 'undefined'"
        :autofillToken="autofillToken"
        :ariaDescribedby="hint ? id + '_hint' : null"
        @ui-change="_onChange"
        @ui-input="_onInput"
        @ui-blur="_onBlur"
        @ui-key-up="_propogate('ui-key-up', $event)"
        @ui-key-press="_propogate('ui-key-press', $event)"
        @ui-key-down="_propogate('ui-key-down', $event)"
      />
    </div>
    <slot name="after-input"></slot>
    <cat-errors v-if="errors" :errors="errors" />
    <div role="presentation" class="spacer"></div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import CatErrors from "@/components/catErrors/catErrors.vue";
import CatInput from "@/components/formelements/cat-input.vue";
import CatHelpTrigger from "@/components/catPageTemplate/catHelpTrigger.vue";
import CatHelp from "@/components/utilities/help.vue";
import CatLabel from "@/components/utilities/cat-label.vue";
import CatHint from "@/components/utilities/hint.vue";
import AttributeMixin from "./attribute-mixin.js";

export default defineComponent({
  name: "CatAttributeText",
  components: {
    CatErrors,
    CatInput,
    CatHelpTrigger,
    CatHelp,
    CatLabel,
    CatHint
  },
  mixins: [AttributeMixin],
  props: {
    id: {
      type: [Boolean, Number, String],
      description: "Sets the ID attributes for child elements."
    },
    blurHandler: {
      type: Function,
      description: "Not in use"
    },
    box: {
      type: Boolean,
      description: "Not used in this component."
    },
    outline: {
      type: Boolean,
      description: "Not used in this component."
    },

    value: {
      type: String,
      description: "Defines the initial default value for the associated field."
    },
    inputType: {
      type: String,
      default: "text",
      description:
        "Defines the type of data the field will accept from the standard HTML options for input. For example: text, number, password. For auto-format numbers, please see cat-attribute-number or cat-attribute-decimal."
    },
    inputMask: {
      type: [String, Array],
      default: "",
      description:
        "The masking library we are using is vue-input-facade and go to https://www.npmjs.com/package/vue-input-facade to read the full documentation." +
        "Supported mask tokens are the folowwing: " +
        "S = alpha characters" +
        "# = numerical characters" +
        "X = alpha numerical characters" +
        "A = alpha characters, transformed to uppercase" +
        "a = alpha characters, transformed to lowercase"
    },
    errors: {
      type: Array,
      description:
        "Sets the error message to be shown on invalid input with the following format: { level: 0, msg: 'This field can not be empty' } where level defines the error level and msg defines the message shown."
    },
    readonly: {
      type: Boolean,
      description:
        "Defines whether a user can input data into the associated field."
    },
    label: {
      type: String,
      description: "Sets the label to display above input fields."
    },
    hint: {
      type: String,
      description: "Sets hint text to appear above input field."
    },
    mandatory: {
      type: Boolean,
      default: false,
      description:
        "Sets aria-required attributes for input fields and label, also sets css class."
    },
    disabled: {
      type: Boolean,
      default: false,
      description:
        "Sets 'disabled' and 'area-disabled' attributes for the input fields elements."
    },
    fullwidth: {
      type: Boolean,
      default: false,
      description: "Adds the fullwidth class to the element."
    },
    showpassword: {
      type: Boolean,
      default: false,
      description:
        "Sets whether the entered password is displayed in plain text."
    },
    placeholder: {
      type: String,
      description: "Sets the placeholder text for a field."
    },
    offscreenLabel: {
      type: String,
      description:
        "Sets the label shown to screen readers for accessibility purposes."
    },
    autofillToken: {
      type: String,
      description:
        "The token to be set to the autocomplete html markup attribute."
    },
    size: {
      type: String,
      description:
        "Adds a class which sets the width of the input box between small, medium, large. Leave undefined for full width."
    },
    inline: {
      type: Boolean,
      default: false,
      description: "Sets css class - styling related."
    },
    mask: {
      type: String,
      description:
        "Defines characters which will be accepted by the input element."
    },
    regex: {
      type: String,
      description: ""
    },
    maxLength: {
      type: Number,
      description: "Sets the maximum number of characters that can be entered."
    },
    help: {
      type: Object,
      description: "Sets the expandable Help Text above an input field."
    }
  },
  data: function () {
    return {
      model: this.value ? this.value : ""
    };
  },
  watch: {
    value: function (newval) {
      if (newval !== this.model) {
        this.model = newval;
      }
    }
  },
  methods: {
    _onChange: function (event) {
      this.$emit("ui-change", {
        event: event.event,
        value: event.value,
        id: this.id ? this.id : null
      });
    },
    _onBlur: function (event) {
      this.$emit("ui-blur", {
        event: event.event,
        value: event.value,
        id: this.id ? this.id : null
      });
    },
    _onInput: function (event) {
      this.model = event.event.target.value; //two way binding
      this.$emit("ui-input", {
        event: event.event,
        value: event.model,
        id: this.id ? this.id : null
      });
    },
    _onInputOrig: function (value) {
      this.$emit("input", value);
    }
  },

  computed: {
    _fs_class: function () {
      var css = {
        fieldset: true,
        "cat-text-field": true,
        invalid:
          this.errors &&
          this.errors.length > 0 &&
          this.errors.some(function (r) {
            return r.level <= 1;
          }),
        required: this.mandatory,
        disabled: this.disabled,
        optional: !this.mandatory,
        inline: this.inline,
        small: this.size == "small",
        medium: this.size == "medium",
        large: this.size == "large",
        "readonly-list": this.readonly,
        fullwidth: this.fullwidth
      };

      css.warning =
        this.errors &&
        this.errors.length > 0 &&
        this.errors.some(function (r) {
          return r.level == 2 && !css.invalid;
        });
      css.infomessage =
        this.errors &&
        this.errors.length > 0 &&
        this.errors.some(function (r) {
          return r.level == 3 && !css.invalid;
        });
      css.debugmessage =
        this.errors &&
        this.errors.length > 0 &&
        this.errors.some(function (r) {
          return r.level == 4;
        });

      return css;
    }
  }
});
</script>
